/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {cirusPrimaryProperties, GenericObject} from '@ion/core/src/types/entities';
import {formatCirusData} from '../../../manufacturer/spreaderUtils';
import {getOtherDetailsCard, getRemainingRows, splitMeasurement, SpreaderLayout, toRow} from './spreaderLayoutShared';

// the Cirus layout's slots, by the readings each one shows - legacy's
// primary Cirus readings ...
const CIRUS_KEYS = {
  tiles : [
    cirusPrimaryProperties.granular,
    cirusPrimaryProperties.dryDistance,
    cirusPrimaryProperties.vehicleDistance
  ],
  conditions : [cirusPrimaryProperties.roadTemperature, cirusPrimaryProperties.airTemperature],
  liquid     : [cirusPrimaryProperties.prewetSetting, cirusPrimaryProperties.antiiceSetting]
};

// the readings the Cirus layout's slots show ...
const CIRUS_USED_KEYS = Object.values(CIRUS_KEYS).flat();

/**
 * Maps a Cirus (or RoadWatch, which legacy renders the same way) message's
 * measures onto the current theme Spreader accordion's Spreader Type 3
 * layout: Granular / Dry Distance / Vehicle Distance tiles, a Conditions
 * card (road, air temp), a Liquid card (pre wet, anti ice) and an Other
 * Details card for any other reading - no status.
 *
 * ```typescript
 *
 *   import {getCirusLayout} from './cirusLayout';
 *   ...
 *
 *   const layout = getCirusLayout(measures);
 *
 * ```
 *
 * @param   measures The Cirus measures.
 * @returns          The accordion layout.
 */
export const getCirusLayout = (measures: GenericObject): SpreaderLayout => {
  const reading = (key: string) => (!isNil(measures?.[key]) ? formatCirusData(key, measures[key]) : undefined);
  const label   = (key: string) => translate(`tracking:spreader.CIRUS.${key}`);
  const tile    = (key: string) => ({label: label(key), ...splitMeasurement(reading(key))});
  const row     = (key: string) => toRow(label(key), reading(key));
  return {
    tiles : CIRUS_KEYS.tiles.map(tile),
    cards : [
      {
        title : translate('tracking:spreader.current.conditions'),
        rows  : CIRUS_KEYS.conditions.map(row)
      },
      {
        title : translate('tracking:spreader.current.liquid'),
        rows  : CIRUS_KEYS.liquid.map(row)
      },
      ...getOtherDetailsCard(getRemainingRows({
        readings : measures,
        usedKeys : CIRUS_USED_KEYS,
        label,
        format   : reading
      }))
    ]
  };
};
